import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { GuiButton } from "../../components/global-ui/gui-button";
import { GuiTextbox } from "../../components/global-ui/gui-textbox";
import { showToast } from "../../components/toast";
import actions from "../../actions/actions";
import { Path, UserRole } from "../../utilities/constants";
import AccountFacebookSection from "./AccountFacebookSection";

function SocialMediaSection() {
  const TWITTER_REDIRECT_URI = `${window.location.origin}${Path.AccountGeneral}`;
  const LINKEDIN_REDIRECT_URI = `${window.location.origin}${Path.AccountGeneral}`;

  const [linkedinStatus, setLinkedInStatus] = useState({
    loading: true,
    connected: false,
    socialConnectionId: 0,
    displayName: "",
  });
  const [linkedinBusy, setLinkedInBusy] = useState(false);
  const [twitterStatus, setTwitterStatus] = useState({
    loading: true,
    connected: false,
    socialConnectionId: 0,
    displayName: "",
  });
  const [twitterBusy, setTwitterBusy] = useState(false);

  const LINKEDIN_OAUTH_STATE_KEY = "linkedinOauthExpectedState";
  const TWITTER_OAUTH_STATE_KEY = "twitterOauthExpectedState";
  const TWITTER_CODE_VERIFIER_KEY = "twitterCodeVerifier";

  useEffect(() => {
    handleLinkedInCallback();
    handleTwitterCallback();
    loadLinkedInStatus();
    loadTwitterStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadLinkedInStatus() {
    const result = await actions.account.getLinkedInStatus();
    if (result?.success) {
      setLinkedInStatus({
        loading: false,
        connected: !!result.connected,
        socialConnectionId: result.socialConnectionId || 0,
        displayName: result.displayName || "",
      });
    } else {
      setLinkedInStatus((prev) => ({ ...prev, loading: false }));
    }
  }

  async function loadTwitterStatus() {
    const result = await actions.account.getTwitterStatus();
    if (result?.success) {
      setTwitterStatus({
        loading: false,
        connected: !!result.connected,
        socialConnectionId: result.socialConnectionId || 0,
        displayName: result.displayName || "",
      });
    } else {
      setTwitterStatus((prev) => ({ ...prev, loading: false }));
    }
  }

  function clearLinkedInCallbackQuery() {
    if (!window.location.search) return;
    window.history.replaceState({}, document.title, Path.AccountGeneral);
  }

  function clearTwitterCallbackQuery() {
    if (!window.location.search) return;
    window.history.replaceState({}, document.title, Path.AccountGeneral);
  }

  async function handleLinkedInCallback() {
    const queryParams = new URLSearchParams(window.location.search);
    const error = queryParams.get("error");
    const errorDescription = queryParams.get("error_description");
    const code = queryParams.get("code");
    const state = queryParams.get("state");

    // Check if this is a LinkedIn callback by verifying expected state exists
    const expectedState = sessionStorage.getItem(LINKEDIN_OAUTH_STATE_KEY);
    if (!expectedState) return; // Not a LinkedIn callback

    if (!error && !code) return;

    if (error) {
      showToast(
        "error",
        decodeURIComponent(
          errorDescription || "LinkedIn sign-in was cancelled.",
        ),
      );
      sessionStorage.removeItem(LINKEDIN_OAUTH_STATE_KEY);
      clearLinkedInCallbackQuery();
      return;
    }

    setLinkedInBusy(true);
    const result = await actions.account.linkedinCallback(
      code,
      state,
      expectedState,
      LINKEDIN_REDIRECT_URI,
    );
    sessionStorage.removeItem(LINKEDIN_OAUTH_STATE_KEY);
    clearLinkedInCallbackQuery();

    if (result?.success) {
      showToast("success", "LinkedIn account connected.");
      await loadLinkedInStatus();
    }

    setLinkedInBusy(false);
  }

  async function handleTwitterCallback() {
    const queryParams = new URLSearchParams(window.location.search);
    const error = queryParams.get("error");
    const errorDescription = queryParams.get("error_description");
    const code = queryParams.get("code");
    const state = queryParams.get("state");

    // Check if this is a Twitter callback by verifying expected state exists
    const expectedState = sessionStorage.getItem(TWITTER_OAUTH_STATE_KEY);
    const codeVerifier = sessionStorage.getItem(TWITTER_CODE_VERIFIER_KEY);
    if (!expectedState) return; // Not a Twitter callback

    if (!error && !code) return;

    if (error) {
      showToast(
        "error",
        decodeURIComponent(
          errorDescription || "Twitter sign-in was cancelled.",
        ),
      );
      sessionStorage.removeItem(TWITTER_OAUTH_STATE_KEY);
      sessionStorage.removeItem(TWITTER_CODE_VERIFIER_KEY);
      clearTwitterCallbackQuery();
      return;
    }

    setTwitterBusy(true);
    const result = await actions.account.twitterCallback(
      code,
      state,
      expectedState,
      TWITTER_REDIRECT_URI,
      codeVerifier, // PKCE code_verifier
    );
    sessionStorage.removeItem(TWITTER_OAUTH_STATE_KEY);
    sessionStorage.removeItem(TWITTER_CODE_VERIFIER_KEY);
    clearTwitterCallbackQuery();

    if (result?.success) {
      showToast("success", "X (Twitter) account connected.");
      await loadTwitterStatus();
    }

    setTwitterBusy(false);
  }

  async function connectLinkedIn() {
    setLinkedInBusy(true);
    const redirectUri = LINKEDIN_REDIRECT_URI;
    const result = await actions.account.connectLinkedIn(redirectUri);

    if (result?.success && result.authorizationUrl) {
      if (result.state) {
        sessionStorage.setItem(LINKEDIN_OAUTH_STATE_KEY, result.state);
      }
      window.location.href = result.authorizationUrl;
      return;
    }

    setLinkedInBusy(false);
  }

  async function disconnectLinkedIn() {
    setLinkedInBusy(true);
    const result = await actions.account.disconnectLinkedIn(
      linkedinStatus.socialConnectionId,
    );
    if (result?.success) {
      showToast("success", "LinkedIn account disconnected.");
      await loadLinkedInStatus();
    }
    setLinkedInBusy(false);
  }

  async function connectTwitter() {
    setTwitterBusy(true);
    const redirectUri = TWITTER_REDIRECT_URI;
    const result = await actions.account.connectTwitter(redirectUri);

    if (result?.success && result.authorizationUrl) {
      if (result.state) {
        sessionStorage.setItem(TWITTER_OAUTH_STATE_KEY, result.state);
      }
      // Store PKCE code_verifier for use in callback
      if (result.codeVerifier) {
        sessionStorage.setItem(TWITTER_CODE_VERIFIER_KEY, result.codeVerifier);
      }
      window.location.href = result.authorizationUrl;
      return;
    }

    setTwitterBusy(false);
  }

  async function disconnectTwitter() {
    setTwitterBusy(true);
    const result = await actions.account.disconnectTwitter(
      twitterStatus.socialConnectionId,
    );
    if (result?.success) {
      showToast("success", "X (Twitter) account disconnected.");
      await loadTwitterStatus();
    }
    setTwitterBusy(false);
  }

  const isLinkedInConnected = linkedinStatus.connected;
  const linkedinActionText = isLinkedInConnected
    ? "Disconnect LinkedIn"
    : "Sign in to LinkedIn";
  const isTwitterConnected = twitterStatus.connected;
  const twitterActionText = isTwitterConnected
    ? "Disconnect X (Twitter)"
    : "Sign in to X (Twitter)";

  return (
    <div className="account-social">
      <div className="account-social__header">
        <span className="account-social__title">Social Media</span>
      </div>

      <div className="account-social__list">
        <AccountFacebookSection />

        {/* LinkedIn */}
        {/* <div className="account-social__item account-social__item--linkedin">
          <div className="account-social__item-header">
            <svg
              className="account-social__icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286ZM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065Zm1.782 13.019H3.555V9h3.564v11.452ZM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003Z"
                fill="#0A66C2"
              />
            </svg>
            <span className="account-social__name">LinkedIn</span>
            <span
              className={`account-social__badge ${isLinkedInConnected ? "account-social__badge--connected" : "account-social__badge--disconnected"}`}
            >
              {isLinkedInConnected
                ? `Connected${linkedinStatus.displayName ? `: ${linkedinStatus.displayName}` : ""}`
                : "Disconnected"}
            </span>
          </div>
          <GuiButton
            className={`btn-outline account-social__connect-btn ${isLinkedInConnected ? "account-social__connect-btn--linkedin-disconnect" : "account-social__connect-btn--linkedin-connect"}`}
            disabled={linkedinBusy || linkedinStatus.loading}
            onClick={isLinkedInConnected ? disconnectLinkedIn : connectLinkedIn}
          >
            {linkedinActionText}
          </GuiButton>
        </div> */}
      </div>
    </div>
  );
}

function LinkedInCredentialsSection({ isAdmin }) {
  const [status, setStatus] = useState({
    loading: true,
    isConfigured: false,
    clientIdMasked: "",
    lastUpdated: null,
  });
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  async function loadStatus() {
    const result = await actions.account.getLinkedInCredentialsStatus();
    if (result?.success) {
      setClientId(result.clientId || "");
      setStatus({
        loading: false,
        isConfigured: !!result.isConfigured,
        clientIdMasked: result.clientIdMasked || "",
        lastUpdated: result.lastUpdated || null,
      });
      return;
    }

    setStatus((prev) => ({ ...prev, loading: false }));
  }

  async function saveCredentials() {
    const trimmedClientId = clientId.trim();
    const trimmedClientSecret = clientSecret.trim();

    if (!trimmedClientId) {
      showToast("error", "Client ID is required.");
      return;
    }

    if (!status.isConfigured && !trimmedClientSecret) {
      showToast("error", "Client Secret is required for first-time setup.");
      return;
    }

    setSaving(true);
    const result = await actions.account.configureLinkedInCredentials(
      trimmedClientId,
      trimmedClientSecret,
    );
    if (result?.success) {
      showToast("success", "LinkedIn credentials saved.");
      setClientSecret("");
      await loadStatus();
    }
    setSaving(false);
  }

  if (!isAdmin) return null;

  const statusText = status.isConfigured ? "Configured" : "Not Configured";
  const lastUpdatedText = status.lastUpdated
    ? new Date(status.lastUpdated).toLocaleString()
    : "N/A";

  return (
    <></>
    // <div className="account-linkedin-credentials">
    //   <div className="account-linkedin-credentials__header">
    //     <span className="account-linkedin-credentials__title">
    //       LinkedIn OAuth Credentials
    //     </span>
    //     <span
    //       className={`account-social__badge ${status.isConfigured ? "account-social__badge--connected" : "account-social__badge--disconnected"}`}
    //     >
    //       {status.loading ? "Loading..." : statusText}
    //     </span>
    //   </div>

    //   <div className="account-linkedin-credentials__form">
    //     <div className="account-linkedin-credentials__field">
    //       <label className="account-linkedin-credentials__label">
    //         Client ID
    //       </label>
    //       <input
    //         type="text"
    //         className="account-linkedin-credentials__input"
    //         value={clientId}
    //         onChange={(e) => setClientId(e.target.value)}
    //         placeholder="Enter LinkedIn Client ID"
    //       />
    //     </div>
    //     <div className="account-linkedin-credentials__field">
    //       <label className="account-linkedin-credentials__label">
    //         Client Secret
    //       </label>
    //       <input
    //         type="password"
    //         className="account-linkedin-credentials__input"
    //         value={clientSecret}
    //         onChange={(e) => setClientSecret(e.target.value)}
    //         placeholder={
    //           status.isConfigured
    //             ? "Saved. Enter a new value only if you want to change it"
    //             : "Enter LinkedIn Client Secret"
    //         }
    //       />
    //     </div>
    //   </div>

    //   <div className="account-linkedin-credentials__meta">
    //     <span>Configured Client ID: {status.clientIdMasked || "N/A"}</span>
    //     <span>Last Updated: {lastUpdatedText}</span>
    //   </div>

    //   <GuiButton
    //     className="account-linkedin-credentials__save-btn"
    //     onClick={saveCredentials}
    //     disabled={saving}
    //   >
    //     {saving ? "Saving..." : "Save LinkedIn Credentials"}
    //   </GuiButton>
    // </div>
  );
}

function DisplaySettingsSection() {
  const [showDescription, setShowDescription] = useState(true);

  return (
    <div className="account-display-settings">
      <div className="account-display-settings__header">
        <span className="account-general__section-title">Display Settings</span>
      </div>

      <div className="account-display-settings__setting">
        <div className="account-display-settings__setting-top">
          <span className="account-display-settings__setting-label">
            Display description instead of name for group filters
          </span>
          <button
            className={`account-social__toggle${showDescription ? " account-social__toggle--on" : ""}`}
            onClick={() => setShowDescription((v) => !v)}
            aria-pressed={showDescription}
          />
        </div>
        <span className="account-display-settings__setting-hint">
          When enabled, group filter dropdowns show the description field.
        </span>
      </div>
    </div>
  );
}

export default function AccountGeneral({ generalData, onSave }) {
  const ADDRESS_MAX_LENGTH = 255;
  const user = useSelector((store) => store.user);
  const fileInputRef = useRef();
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [accountInfo, setAccountInfo] = useState(null);
  const [editAddress, setEditAddress] = useState("");
  const [showFilterGroupDesc, setShowFilterGroupDesc] = useState(false);
  const isAdmin = user?.userType === UserRole.Admin;

  // Seed local state when parent provides data
  useEffect(() => {
    if (generalData) setAccountInfo(generalData);
  }, [generalData]);

  // Seed the address value when entering edit mode
  const startEditing = () => {
    const addr = (accountInfo?.customerAddress || "").slice(
      0,
      ADDRESS_MAX_LENGTH,
    );
    setEditAddress(addr);
    setIsEditing(true);
  };

  useEffect(() => {
    if (!isAdmin) return;
    actions.account.getDisplaySettings().then((result) => {
      if (result) setShowFilterGroupDesc(result.showFilterGroupDesc ?? false);
    });
  }, [isAdmin]);

  function handleLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const logoFile = fileInputRef.current?.files?.[0] || null;
      const result = await actions.account.updateAccountGeneral(
        { CustomerAddress: editAddress.trim() },
        logoFile,
      );
      if (!result) return;

      // Reflect saved values in local state immediately for instant feedback
      setAccountInfo((prev) => ({
        ...prev,
        customerAddress: editAddress.trim(),
        ...(result.accountLogoUrl
          ? { accountLogoUrl: result.accountLogoUrl }
          : {}),
      }));
      showToast("success", "Basic Information Updated.");
      setIsEditing(false);
      setLogoPreview(null);
      onSave?.();
    } finally {
      setSaving(false);
    }
  }

  async function handleDisplaySettingChange(_, checked) {
    setShowFilterGroupDesc(checked);
    const result = await actions.account.saveDisplaySettings(checked);
    if (!result?.success) {
      setShowFilterGroupDesc(!checked);
      showToast("error", "Failed to save display setting.");
    }
  }

  const productName = accountInfo?.productName || "\u2014";
  const customerAddress = accountInfo?.customerAddress || "\u2014";

  return (
    <div className="account-sections">
      {/* Basic Information card — toggles between view and edit */}
      <div className="account-general">
        <div className="account-general__section-header account-general__section-header--row">
          <span className="account-general__section-title">
            Basic Information
          </span>
          {!isEditing && (
            <GuiButton
              className="btn-outline account-general__edit-btn"
              onClick={startEditing}
            >
              <i className="fas fa-pen" /> Edit
            </GuiButton>
          )}
        </div>

        {/* Row 1: Product + Address */}
        <div className="account-general__row">
          <div className="account-general__field">
            <span className="account-general__label">Product</span>
            {isEditing ? (
              <div className="account-general__input-display">
                {productName}
              </div>
            ) : (
              <span className="account-general__value">{productName}</span>
            )}
          </div>
          <div
            className={`account-general__field${isEditing ? " account-general__field--wide" : ""}`}
          >
            <span className="account-general__label">Address</span>
            {isEditing ? (
              <GuiTextbox
                className="account-general__address-textbox"
                name="customerAddress"
                value={editAddress}
                onChange={(_, value) => setEditAddress(value)}
                maxLength={ADDRESS_MAX_LENGTH}
              />
            ) : (
              <span className="account-general__value">{customerAddress}</span>
            )}
          </div>
        </div>

        {/* Row 2: Account Logo */}
        <div className="account-general__row">
          <div className="account-general__field">
            <span className="account-general__label">Account Logo</span>
            {isEditing ? (
              <>
                {(logoPreview || accountInfo?.accountLogoUrl) && (
                  <div className="account-general__logo-preview">
                    <img
                      src={logoPreview || accountInfo?.accountLogoUrl}
                      alt="Account logo"
                      className="account-general__logo-img"
                    />
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="account-general__file-input"
                  onChange={handleLogoChange}
                />
                <GuiButton
                  className="btn-outline account-general__upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <i className="fas fa-upload" /> Upload Image
                </GuiButton>
              </>
            ) : accountInfo?.accountLogoUrl ? (
              <img
                src={accountInfo.accountLogoUrl}
                alt="Account logo"
                className="account-general__logo-img"
              />
            ) : (
              <span className="account-general__value account-general__value--dim">
                No File Chosen
              </span>
            )}
          </div>
        </div>

        {/* Cancel / Save — only visible in edit mode, inside this card */}
        {isEditing && (
          <div className="account-general__actions">
            <GuiButton
              className="btn-outline account-general__cancel-btn"
              disabled={saving}
              onClick={() => {
                setIsEditing(false);
                setLogoPreview(null);
                setEditAddress("");
              }}
            >
              Cancel
            </GuiButton>
            <GuiButton
              className="account-general__save-btn"
              disabled={saving}
              onClick={handleSave}
            >
              <i className="fas fa-save" /> {saving ? "Saving\u2026" : "Save"}
            </GuiButton>
          </div>
        )}
      </div>

      {/* These sections are always view-only */}
      <SocialMediaSection />
      <LinkedInCredentialsSection isAdmin={isAdmin} />

      {isAdmin && (
        <div className="account-display-settings">
          <div className="account-display-settings__header">
            <span className="account-display-settings__title">
              Display Settings
            </span>
          </div>
          <div className="account-display-settings__setting">
            <div className="account-display-settings__setting-top">
              <span className="account-display-settings__setting-label">
                Display description instead of name for group filters
              </span>
              <button
                className={`account-social__toggle${showFilterGroupDesc ? " account-social__toggle--on" : ""}`}
                onClick={() =>
                  handleDisplaySettingChange(null, !showFilterGroupDesc)
                }
                aria-pressed={showFilterGroupDesc}
              />
            </div>
            <span className="account-display-settings__setting-hint">
              When enabled, group filter dropdowns show the description field.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
